import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import { Navbar } from "@/components/Navbar";
import { HeroCarousel } from "@/components/HeroCarousel";
import { FeaturedEventModal } from "@/components/FeaturedEventModal";
import { FeaturedBirthdayModal } from "@/components/FeaturedBirthdayModal";
import { PrivateAreaModal } from "@/components/PrivateAreaModal";
import { AgeGate } from "@/components/AgeGate";
import { syncPushNotifications } from "@/lib/pushNotifications";
import api from "@/lib/api";
import { ADMIN_PANEL_PATH, LIST_PANEL_PATH, STOCK_PANEL_PATH, isListPanelHost } from "@/lib/adminRoute";

const VideoShowcase = lazy(() => import("@/components/VideoShowcase").then((m) => ({ default: m.VideoShowcase })));
const AboutSection = lazy(() => import("@/components/AboutSection").then((m) => ({ default: m.AboutSection })));
const EnvironmentsSection = lazy(() => import("@/components/EnvironmentsSection").then((m) => ({ default: m.EnvironmentsSection })));
const EventsSection = lazy(() => import("@/components/EventsSection").then((m) => ({ default: m.EventsSection })));
const BirthdaysSection = lazy(() => import("@/components/BirthdaysSection").then((m) => ({ default: m.BirthdaysSection })));
const LocationSection = lazy(() => import("@/components/LocationSection").then((m) => ({ default: m.LocationSection })));
const ContactSection = lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection })));
const Footer = lazy(() => import("@/components/Footer").then((m) => ({ default: m.Footer })));
const NotificationPrompt = lazy(() => import("@/components/NotificationPrompt").then((m) => ({ default: m.NotificationPrompt })));

// Rotas secundárias — carregadas só quando o usuário navegar para elas
const AdminPanel     = lazy(() => import("@/components/AdminPanel"));
const ListAdminPanel = lazy(() => import("@/components/ListAdminPanel").then((m) => ({ default: m.ListAdminPanel })));
const AdminStockPanel = lazy(() => import("@/components/AdminStockPanel").then((m) => ({ default: m.AdminStockPanel })));
const RulesPage      = lazy(() => import("@/components/RulesPage").then((m) => ({ default: m.RulesPage })));
const GuestListPage  = lazy(() => import("@/components/GuestListPage").then((m) => ({ default: m.GuestListPage })));
const TermsPage      = lazy(() => import("@/components/TermsPage").then((m) => ({ default: m.TermsPage })));
const EventPage      = lazy(() => import("@/components/EventPage").then((m) => ({ default: m.EventPage })));
const BirthdayPage   = lazy(() => import("@/components/BirthdayPage").then((m) => ({ default: m.BirthdayPage })));

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-[#050505]">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#CC0000] border-t-transparent" />
  </div>
);

const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.18, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

const removePrerenderSplash = () => {
  const el = document.getElementById("sc-prerender");
  if (!el) return;
  el.style.opacity = "0";
  setTimeout(() => el.remove(), 350);
};

const HomeSectionsFallback = () => (
  <div className="min-h-[720px] bg-[#050505]" aria-hidden="true" />
);

const HomePage = () => {
  const [loadRest, setLoadRest] = useState(false);
  const [eventModalSettled, setEventModalSettled] = useState(false);

  useEffect(() => {
    let resolved = false;
    const loadDeferredWork = () => {
      if (resolved) return;
      resolved = true;
      setLoadRest(true);
      syncPushNotifications();
    };
    const scheduleIdle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 900));
    const cancelIdle = window.cancelIdleCallback || window.clearTimeout;
    const idleId = scheduleIdle(loadDeferredWork);
    window.addEventListener("scroll", loadDeferredWork, { once: true, passive: true });
    window.addEventListener("touchstart", loadDeferredWork, { once: true, passive: true });

    return () => {
      cancelIdle(idleId);
      window.removeEventListener("scroll", loadDeferredWork);
      window.removeEventListener("touchstart", loadDeferredWork);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="app-root">
      <div className="noise-overlay" />
      <Navbar />
      <FeaturedEventModal onSettled={() => setEventModalSettled(true)} />
      <FeaturedBirthdayModal ready={eventModalSettled} />
      <HeroCarousel />
      <Suspense fallback={<HomeSectionsFallback />}>
        {loadRest ? (
          <>
            <VideoShowcase />
            <AboutSection />
            <EnvironmentsSection />
            <EventsSection />
            <BirthdaysSection />
            <LocationSection />
            <ContactSection />
            <Footer />
            <NotificationPrompt />
          </>
        ) : (
          <HomeSectionsFallback />
        )}
      </Suspense>
    </div>
  );
};

const SecurePanelEntry = ({ allowedRoles, children, loginTitle, loginDescription, redirectPath }) => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (!localStorage.getItem("admin_session")) {
        localStorage.removeItem("admin_session");
        setChecking(false);
        setAllowed(false);
        setLoginOpen(true);
        return;
      }

      try {
        const res = await api.get("/me", { suppressAuthRedirect: true });
        const canAccess = allowedRoles.includes(res.data.role);
        setAllowed(canAccess);
        if (!canAccess) {
          localStorage.removeItem("admin_session");
          setLoginOpen(true);
        }
      } catch (err) {
        const status = err.response?.status;
        if (!status || status >= 500) {
          // Erro de rede ou servidor fora do ar — não deslogar, cookie pode ainda ser válido
          setAllowed(true);
        } else {
          localStorage.removeItem("admin_session");
          setAllowed(false);
          setLoginOpen(true);
        }
      } finally {
        setChecking(false);
      }
    };

    checkAccess();
  }, [allowedRoles]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#CC0000] border-t-transparent" />
      </div>
    );
  }

  if (allowed) {
    return children;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <PrivateAreaModal
        open={loginOpen}
        onOpenChange={(open) => {
          if (!open && !localStorage.getItem("admin_session")) {
            setLoginOpen(true);
            return;
          }
          setLoginOpen(open);
        }}
        redirectPath={redirectPath}
        title={loginTitle}
        description={loginDescription}
      />
    </div>
  );
};

const AdminEntry = () => (
  <SecurePanelEntry
    allowedRoles={["admin"]}
    loginTitle="Área ADM"
    loginDescription="Acesso exclusivo ADMIN"
    redirectPath={ADMIN_PANEL_PATH}
  >
    <AdminPanel />
  </SecurePanelEntry>
);

const ListAdminEntry = () => (
  <SecurePanelEntry
    allowedRoles={["admin", "list_admin"]}
    loginTitle="Painel da Lista"
    loginDescription="Acesso operacional para conferência de convidados"
    redirectPath={LIST_PANEL_PATH}
  >
    <ListAdminPanel />
  </SecurePanelEntry>
);

const StockAdminEntry = () => (
  <SecurePanelEntry
    allowedRoles={["admin", "stock_admin"]}
    loginTitle="Estoque"
    loginDescription="Acesso exclusivo para controle de estoque"
    redirectPath={STOCK_PANEL_PATH}
  >
    <AdminStockPanel />
  </SecurePanelEntry>
);

const ConditionalAgeGate = () => {
  const { pathname } = useLocation();
  if (pathname === ADMIN_PANEL_PATH || pathname === LIST_PANEL_PATH || pathname === STOCK_PANEL_PATH) return null;
  return <AgeGate />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
        <Route path="/lista" element={<PageWrapper><GuestListPage /></PageWrapper>} />
        <Route path="/regras" element={<PageWrapper><RulesPage /></PageWrapper>} />
        <Route path="/termos" element={<PageWrapper><TermsPage /></PageWrapper>} />
        <Route path="/eventos/:id" element={<PageWrapper><EventPage /></PageWrapper>} />
        <Route path="/aniversario/id/:id" element={<PageWrapper><BirthdayPage /></PageWrapper>} />
        <Route path="/aniversario/:token" element={<PageWrapper><BirthdayPage /></PageWrapper>} />
        <Route path={ADMIN_PANEL_PATH} element={<AdminEntry />} />
        <Route path={LIST_PANEL_PATH} element={<ListAdminEntry />} />
        <Route path={STOCK_PANEL_PATH} element={<StockAdminEntry />} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    removePrerenderSplash();
  }, []);

  useEffect(() => {
    const onRipple = (e) => {
      if (window.matchMedia("(hover: none)").matches) return;
      const target = e.target.closest(
        ".btn-premium, .btn-premium-rounded, .btn-gold-rounded, .hero-cta-premium, .hero-cta-gold"
      );
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const d = Math.max(target.offsetWidth, target.offsetHeight);
      const span = document.createElement("span");
      span.className = "ripple-effect";
      span.style.width = `${d}px`;
      span.style.height = `${d}px`;
      span.style.left = `${e.clientX - rect.left - d / 2}px`;
      span.style.top = `${e.clientY - rect.top - d / 2}px`;
      target.appendChild(span);
      span.addEventListener("animationend", () => span.remove(), { once: true });
    };
    document.addEventListener("click", onRipple);
    return () => document.removeEventListener("click", onRipple);
  }, []);

  if (isListPanelHost()) {
    return (
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="*" element={<ListAdminEntry />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <AnimatedRoutes />
      </Suspense>
      <ConditionalAgeGate />
    </BrowserRouter>
  );
}

export default App;
